import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSelectedBankSwitchingRadar, publicDemoModel } from '@/data/publicDemoModel';
import { CustomerSwitchingRadar } from '@/components/analytics/CustomerSwitchingRadar';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const DemoDashboardPage: React.FC = () => {
  const { t } = useTranslation(['navigation', 'landing', 'common', 'filters']);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [selectedRadarBank, setSelectedRadarBank] = useState(() => publicDemoModel.switchingRadarBankOptions[0] || 'BK');
  const switchingRadar = useMemo(
    () => getSelectedBankSwitchingRadar(selectedRadarBank),
    [selectedRadarBank],
  );

  const openLockedModal = () => setShowLockedModal(true);
  const maxPrimaryShare = Math.max(...Object.values(publicDemoModel.competitivePressureByBank));

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Header — matches PublicSiteHeader style */}
      <header className="sticky top-0 z-30 border-b border-[#E2E8F0] bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link to="/" className="flex-shrink-0" aria-label="BrandEdge home">
            <img
              src="/BrandEdge_Web_Pack/brandedge-logo-white-bg-original.png"
              alt="BrandEdge"
              className="h-9 w-auto sm:h-10"
              style={{ objectFit: 'contain' }}
              draggable={false}
            />
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.2em] text-[#94A3B8] sm:block">
              {t('navigation:demoDashboard')}
            </span>
            <div className="ml-3 flex items-center gap-2">
              <LanguageSwitcher variant="dark" />
              <button
                onClick={openLockedModal}
                className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A]"
              >
                {t('filters:filters')}
              </button>
              <button
                onClick={openLockedModal}
                className="hidden rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A] sm:block"
              >
                {t('common:export')}
              </button>
              <button
                onClick={openLockedModal}
                className="hidden rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A] sm:block"
              >
                {t('common:aiChat')}
              </button>
              <Link
                to="/login"
                className="rounded-lg bg-[#E10613] px-3 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
              >
                {t('navigation:requestDashboardAccess')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">

        {/* Demo mode notice */}
        <section className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-medium text-[#991B1B]">
          {t('landing:demoModeNotice', { country: publicDemoModel.country })}
        </section>

        {/* BrandEdge Score */}
        <section className="mt-6 rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t('navigation:headlineKpi')}</p>
          <h1 className="mt-2 text-xl font-black text-[#0F172A]">{t('navigation:brandEdgeScore')}</h1>
          <div className="mt-3 flex items-end gap-3">
            <p className="text-5xl font-black text-[#E10613]">{publicDemoModel.brandEdgeScore}</p>
            <p className="pb-1 text-lg font-semibold text-[#64748B]">/ 100</p>
          </div>
          <p className="mt-2 text-sm text-[#475569]">{publicDemoModel.brandEdgeLabel}</p>
        </section>

        {/* Executive Snapshot */}
        <section className="mt-6">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t('navigation:executiveSnapshot')}</h2>
          <div className="mt-3 grid gap-4 md:grid-cols-4">
            <article className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:multiBankCustomers')}</p>
              <p className="mt-2 text-3xl font-black text-[#0F172A]">{publicDemoModel.executiveSnapshot.multiBankShare}%</p>
            </article>
            <article className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:avgBanksPerCustomer')}</p>
              <p className="mt-2 text-3xl font-black text-[#0F172A]">{publicDemoModel.executiveSnapshot.avgBanksPerCustomer}</p>
            </article>
            <article className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:primaryBankShareLeader')}</p>
              <p className="mt-2 text-3xl font-black text-[#0F172A]">{publicDemoModel.executiveSnapshot.primaryBankLeaderShare}%</p>
            </article>
            <article className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:topSecondChoiceCompetitor')}</p>
              <p className="mt-2 text-3xl font-black text-[#0F172A]">{publicDemoModel.executiveSnapshot.topSecondChoiceCompetitor}</p>
            </article>
          </div>
        </section>

        {/* Competitive Pressure + Customer Behavior */}
        <section className="mt-6 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <article className="rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t('navigation:competitivePressureView')}</h2>
            <div className="mt-4 space-y-3">
              {Object.entries(publicDemoModel.competitivePressureByBank).map(([bank, share]) => (
                <div key={bank}>
                  <div className="mb-1 flex justify-between text-xs font-medium text-[#344054]">
                    <span>{bank}</span>
                    <span>{share}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#F1F5F9]">
                    <div className="h-2 rounded-full bg-[#E10613]" style={{ width: `${(share / maxPrimaryShare) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:secondChoicePressure')}</p>
                <p className="mt-2 text-base font-bold text-[#0F172A]">
                  {publicDemoModel.executiveSnapshot.secondChoicePressureLabel} ({publicDemoModel.executiveSnapshot.topSecondChoiceCompetitor})
                </p>
              </div>
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:switchingRiskIndicator')}</p>
                <p className="mt-2 text-base font-bold text-[#0F172A]">{publicDemoModel.switchingRisk}%</p>
              </div>
            </div>
          </article>

          <article className="rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t('navigation:customerBehaviorModule')}</h2>
            <div className="mt-4 space-y-3">
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:awareness')}</p>
                <p className="mt-2 text-2xl font-black text-[#0F172A]">{publicDemoModel.brandEdgePreview.awareness}%</p>
              </div>
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:activeUsage')}</p>
                <p className="mt-2 text-2xl font-black text-[#0F172A]">{publicDemoModel.brandEdgePreview.currentUsage}%</p>
              </div>
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:loyaltyIndex')}</p>
                <p className="mt-2 text-2xl font-black text-[#0F172A]">{publicDemoModel.brandEdgePreview.loyaltyIndex}%</p>
              </div>
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{t('navigation:multiBankShare')}</p>
                <p className="mt-2 text-2xl font-black text-[#0F172A]">{publicDemoModel.brandEdgePreview.multiBankShare}%</p>
              </div>
            </div>
          </article>
        </section>

        {/* Customer Switching Radar */}
        <section className="mt-6">
          <CustomerSwitchingRadar
            metrics={switchingRadar}
            title="Customer Switching Radar"
            subtitle="Illustrative sample view of where competitors are most likely to pull customers from the selected bank."
            headerRight={(
              <label className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">
                {t('navigation:radarSampleBank')}
                <select
                  value={selectedRadarBank}
                  onChange={(event) => setSelectedRadarBank(event.target.value)}
                  className="mt-2 block rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-xs font-bold text-[#0F172A] focus:border-[#E10613] focus:outline-none"
                >
                  {publicDemoModel.switchingRadarBankOptions.map((bankId) => (
                    <option key={bankId} value={bankId}>{bankId}</option>
                  ))}
                </select>
              </label>
            )}
          />
        </section>

        {/* Score Trend */}
        <section className="mt-6 rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t('navigation:trendPreview')}</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {publicDemoModel.scoreTrend.map((point) => (
              <article key={point.quarter} className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{point.quarter}</p>
                <p className="mt-2 text-2xl font-black text-[#0F172A]">{point.score}</p>
              </article>
            ))}
          </div>
          <p className="mt-3 text-xs font-semibold text-[#E10613]">
            {t('landing:illustrativeMomentum', { points: publicDemoModel.brandEdgePreview.momentumTrend })}
          </p>
        </section>

        {/* CTA */}
        <section className="mt-8 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-6">
          <h2 className="text-lg font-black text-[#0F172A]">{t('navigation:exploreBrandEdge')}</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="rounded-lg bg-[#E10613] px-5 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
            >
              {t('navigation:requestDashboardAccess')}
            </Link>
            <Link
              to="/"
              className="rounded-lg border border-[#FCA5A5] bg-white px-5 py-3 text-xs font-bold uppercase tracking-widest text-[#E10613] hover:bg-[#FEF2F2]"
            >
              {t('navigation:returnToBrandEdge')}
            </Link>
          </div>
        </section>
      </main>

      {/* Locked feature modal */}
      {showLockedModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#0F172A]/50 p-6">
          <div className="w-full max-w-md rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center shadow-xl">
            <h3 className="text-lg font-black text-[#0F172A]">{t('landing:featureLockedTitle')}</h3>
            <p className="mt-3 text-sm text-[#475569]">{t('landing:featureLockedBody')}</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link
                to="/login"
                className="rounded-lg bg-[#E10613] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
              >
                {t('navigation:requestDashboardAccess')}
              </Link>
              <button
                onClick={() => setShowLockedModal(false)}
                className="rounded-lg border border-[#E2E8F0] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1]"
              >
                {t('common:close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoDashboardPage;
